import React from 'react';
import {LngLat} from 'mapbox-gl';

export const dataSizeString = (kb: number) => {
    const gb = kb > 1024 * 1024;
    const unitString = gb ? 'gb' : 'mb';
    const formattedSize = gb ? kb / 1024 / 1024 : kb / 1024;

    return `${formattedSize.toFixed(2)} ${unitString}`;
};

export const metersToUsersUnits = (meters: number, user: User | undefined) => {
    if (user && user.unitSystem === 'metric') {
        return meters;
    }
    return meters * 3.281;
};

export const metersToUsersDistanceUnits = (meters: number, user: User | undefined) => {
    if (user && user.unitSystem === 'metric') {
        return meters / 1000.0;
    }
    return meters * 3.281 / 5280;
};

export const formatDistance = (meters: number, user: User | undefined) => {
    let convertedMeters = meters;
    if (isNaN(convertedMeters)) {
        convertedMeters = 0;
    }
    let decimalPlaces = 2;
    if (user && user.unitSystem === 'metric') {
        if (meters < 500) {
            return `${convertedMeters.toFixed(0)} m`;
        }
        const km = convertedMeters / 1000.0;
        if (km > 10) {
            decimalPlaces = 1;
        }
        if (km > 1000) {
            decimalPlaces = 0;
        }
        return `${km.toFixed(decimalPlaces)} km`;
    }

    const feet = convertedMeters * 3.281;
    if (feet < 500) {
        return `${feet.toFixed(0)} ft`;
    }
    const miles = feet / 5280;
    if (miles > 10) {
        decimalPlaces = 1;
    }
    if (miles > 1000) {
        decimalPlaces = 0;
    }
    return `${miles.toFixed(decimalPlaces)} mi`;
};

export const formatArea = (meters: number, user: User | undefined) => {
    let convertedMeters = meters;
    if (isNaN(convertedMeters)) {
        convertedMeters = 0;
    }
    let decimalPlaces = 2;

    if (user && user.unitSystem === 'metric') {
        if (meters < 1000000) {
            return `${convertedMeters.toFixed(0)} m²`;
        }
        const km = convertedMeters / 1000000.0;
        if (km > 10) {
            decimalPlaces = 1;
        }
        if (km > 100) {
            decimalPlaces = 0;
        }
        return `${km.toFixed(decimalPlaces)} km²`;
    }

    const acres = convertedMeters * 0.000247105;
    if (acres > 10) {
        decimalPlaces = 1;
    }
    if (acres > 1000) {
        decimalPlaces = 0;
    }
    if (acres > 1000000) {
        return `${(acres / 1000000).toFixed(decimalPlaces)} million acres`;
    } else if (acres > 10000) {
        return `${(acres / 1000).toFixed(decimalPlaces)}k acres`;
    }
    return `${acres.toFixed(decimalPlaces)} acres`;
};

export const formatElevation = (meters: number, user: User | undefined) => {
    let convertedMeters = meters;
    if (!meters || isNaN(meters)) {
        convertedMeters = 0;
    }

    if (user && user.unitSystem === 'metric') {
        return `${Math.round(convertedMeters).toLocaleString()} m`;
    }
    return `${Math.round(convertedMeters * 3.281).toLocaleString()} ft`;
};

export const formatTimeWithHours = (seconds: number) => {
    const hours = (seconds / 3600).toFixed(0);
    const minutes = Math.round(seconds / 60) % 60;
    let minutesString = minutes.toFixed(0);
    minutesString = `${minutesString} min`;
    if (hours === '0') {
        return minutesString;
    }
    return `${hours} hrs, ${minutesString}`;
};

// http://stackoverflow.com/questions/23618744/rendering-comma-separated-list-of-links
const intersperse = (arr: any[], sep: string) => {
    if (arr.length === 0) {
        return [];
    }
    return arr.slice(1).reduce((xs, x) => xs.concat([sep, x]), [arr[0]]);
};

// returns JSX for a comma seperate list of URL/title pairs
export const formatWebsiteNodes = (websites: {link: string; text: string}[]) => {
    if (!websites || websites.length === 0) {
        return null;
    }
    const linkHtml = intersperse(
        websites.map((website, i) => (
            <a key={i} href={website.link} target='_new'>
                {website.text}
            </a>
        )),
        ', '
    );
    return linkHtml;
};

export const formatCoordinates = (coordinates: number[], precision = 5) => {
    coordinates = LngLat.convert(coordinates as mapboxgl.LngLatLike).toArray();
    return `${coordinates[1].toFixed(precision)}, ${coordinates[0].toFixed(precision)}`;
};

export function isFormattedCoordinates(string: string) {
    return string.match(/-?[0-9]+\.[0-9]+,\W-?[0-9]+\.[0-9]+/);
}

const monthLetterMap = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
export const formatMonth = (monthIndex: number) => {
    return monthLetterMap[monthIndex];
};

const monthAbbrevMap = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
];

export const formatMonthAbbrev = (monthIndex: number) => {
    return monthAbbrevMap[monthIndex];
};
